import { useMemo } from 'react';
import { AlertCircle, CheckCircle2 } from '@vitness/icons';
import type { Expense, FundingSchedule, IncomeStreams } from '@shared/types';
import {
  checksInMonth, guaranteedChecksPerMonth, indexSchedules, monthlyFactor, scheduleFor,
} from '@shared/calendar';
import { perCheckOf, isGrossStream } from '@shared/income';
import { fmtMoney, fmtPct } from '../../lib/format';
import { Num } from '../../components/ui';

/**
 * The paycheck cadences, and whether each one covers what it funds.
 *
 * Two schedules being live is the normal, permanent state here, not a mistake:
 * HSA is funded twice a month and deliberately skips the third paycheck of a
 * 3-paycheck month, so it sits on a 24-cadence while the pay behind it still
 * arrives 26 times a year. The app spent a long time collapsing that into one
 * `min(floor(N/12))` scalar — which is right for the 24-line and 7.69% short
 * for everything else — so making the split visible is half the point of this
 * panel.
 *
 * The other half is coverage. `takeHomeTotal` sums every set-aside against one
 * blended net, so a cadence whose obligations exceed its own take-home is
 * completely invisible today: it hides inside an average that reports
 * everything as fine. Per-item schedules make the check fall straight out.
 */
export function FundingSchedulesPanel({ schedules, expenses, income, now = new Date() }: {
  schedules: FundingSchedule[];
  expenses: Expense[];
  income: IncomeStreams;
  now?: Date;
}) {
  const rows = useMemo(() => {
    const index = indexSchedules(schedules);
    const year = now.getUTCFullYear();
    const month = now.getUTCMonth();

    return schedules
      .filter(s => s.isActive)
      .map(s => {
        const funded = expenses.filter(e =>
          (e.status ?? 'A') === 'A' && scheduleFor(index, e.fundingScheduleId, schedules)?.id === s.id);
        const draw = funded.reduce((sum, e) => sum + (Number(e.saapRounded) || 0), 0);

        // Pay attached to this cadence: the schedule's own figure, or any
        // income stream keyed to it. A cadence can legitimately have neither.
        const streamPay = income.sources
          .filter(x => x.isActive && !isGrossStream(x.name) && x.fundingScheduleId === s.id)
          .reduce((sum, x) => sum + perCheckOf(x, schedules), 0);
        const netPerCheck = s.netPerCheck > 0 ? s.netPerCheck : streamPay;

        return {
          s,
          funded: funded.length,
          draw,
          netPerCheck,
          annualNet: netPerCheck * s.paycheckCount,
          checksThisMonth: checksInMonth(s, year, month),
          // Positional cadences (24, 12) are the same every month and never
          // need an anchor; only interval ones (26, 52) do.
          isInterval: !Number.isInteger(s.paycheckCount / 12),
          // Undefined rather than 0 when there is no pay — "this cadence is a
          // funding rhythm, not a job" is a different statement from "this job
          // cannot afford its obligations", and rendering the second when the
          // first is true would be a permanent false alarm.
          coverage: netPerCheck > 0 ? draw / netPerCheck : null,
        };
      });
  }, [schedules, expenses, income, now]);

  if (!rows.length) {
    return (
      <div style={{ fontSize: 12.5, color: 'var(--vn-text-muted)', lineHeight: 1.6 }}>
        No active paycheck cadences have synced from Notion yet.
      </div>
    );
  }

  return (
    <>
      <div className="vd-fs-list">
        {rows.map(r => {
          const over = r.coverage !== null && r.coverage > 1;
          return (
            <div key={r.s.id} className="vd-fs-row">
              <div className="vd-fs-row__head">
                <span className="vd-fs-row__name">{r.s.name}</span>
                <Num className="vd-fs-row__cadence">
                  {r.s.frequency} · {r.s.paycheckCount}/yr
                </Num>
              </div>

              <Num className="vd-fs-row__facts">
                {/* The two factors, side by side and named. They are equal only
                    when the cadence is 24, and that equality is exactly what
                    made one scalar look like it worked for both. */}
                ×{monthlyFactor(r.s).toFixed(4).replace(/0+$/, '').replace(/\.$/, '')} per month
                {' · '}
                {guaranteedChecksPerMonth(r.s)} guaranteed
                {' · '}
                {r.checksThisMonth} this month
                {r.checksThisMonth > guaranteedChecksPerMonth(r.s) && (
                  <strong style={{ color: 'var(--vn-success)' }}> — extra paycheck</strong>
                )}
                {!r.s.anchorDate && r.isInterval && (
                  <span style={{ color: 'var(--vn-warning)' }}> · no anchor date</span>
                )}
              </Num>

              <Num className="vd-fs-row__money">
                {r.netPerCheck > 0
                  ? <>{fmtMoney(r.netPerCheck)}/pay · {fmtMoney(r.annualNet)}/yr</>
                  : <span style={{ color: 'var(--vn-text-muted)' }}>no pay on this cadence</span>}
              </Num>

              <Num className="vd-fs-row__draw">
                {r.funded} line{r.funded === 1 ? '' : 's'} · {fmtMoney(r.draw)}/pay drawn
              </Num>

              {/*
                 An active cadence funding nothing is almost always a job that
                 ended and never got switched off. Harmless to the totals — no
                 line points at it — but it makes `scheduleFor` ambiguous for
                 any expense whose relation is blank, which then falls back to
                 the 24 default instead of the cadence you actually use.
              */}
              {r.funded === 0 && (
                <span className="vd-fs-row__cov is-over">
                  <AlertCircle size={12} />
                  active but funds nothing — switch it off in Notion if the job has ended
                </span>
              )}
              {r.coverage !== null && (
                <span className={`vd-fs-row__cov${over ? ' is-over' : ''}`}>
                  {over ? <AlertCircle size={12} /> : <CheckCircle2 size={12} />}
                  {fmtPct(r.coverage * 100, 0)} of take-home
                  {over && ' — this cadence cannot fund its own lines'}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 11.5, color: 'var(--vn-text-muted)', lineHeight: 1.65, marginTop: 12 }}>
        A cadence is a funding rhythm, not necessarily a job — a 24-row can exist so a line skips the
        third paycheck of a 3-paycheck month while the pay behind it still arrives 26 times a year.
        Coverage compares what each cadence draws per paycheck against the take-home landing on it; it
        is the check a single blended average can never make.
      </div>
    </>
  );
}
